// usePointingState — Phase 2.5d: element-pointing cue.
//
// The agent emits `signal_stage_event({event:'point_at', target:'X'})` between
// sentences to highlight a specific item inside the current scene. The host
// page (voice_agent.html) translates that into a window CustomEvent:
//
//   - concierge:point_at { detail: { target: string | null } }
//
// The orb morphs to that element via Framer Motion `layoutId="orb"`. When
// pointing clears (a new storyboard mounts, the agent dispatches point_at
// with target=null, or a barge-in interrupts), the default per-template
// anchor takes over again.
//
// Intentional clears:
//   - concierge:storyboard — new scene set, prior pointing no longer relevant
//   - concierge:interrupt  — barge-in, orb returns to safe default
//
// We do NOT clear on concierge:audio_done — the agent may keep pointing at
// the last item while it pauses for a beat before the next sentence.

import { useEffect, useState } from 'react';

export function usePointingState(): { currentTarget: string | null } {
  const [currentTarget, setCurrentTarget] = useState<string | null>(null);
  useEffect(() => {
    const onPoint = (e: Event) => {
      const detail = (e as CustomEvent<{ target?: string | null }>).detail;
      const t = detail && typeof detail.target === 'string' && detail.target.length > 0
        ? detail.target
        : null;
      setCurrentTarget(t);
    };
    const onClear = () => setCurrentTarget(null);
    window.addEventListener('concierge:point_at',  onPoint);
    window.addEventListener('concierge:storyboard', onClear);
    window.addEventListener('concierge:interrupt',  onClear);
    return () => {
      window.removeEventListener('concierge:point_at',  onPoint);
      window.removeEventListener('concierge:storyboard', onClear);
      window.removeEventListener('concierge:interrupt',  onClear);
    };
  }, []);
  return { currentTarget };
}
